import React from 'react';

/** Mobil = under 768 px, samma brytpunkt som appens `@media (max-width: 767px)`. */
export const MOBILE_QUERY = '(max-width: 767px)';

export function useIsMobile(): boolean {
  const get = () => typeof window !== 'undefined' && !!window.matchMedia?.(MOBILE_QUERY).matches;
  const [mob, setMob] = React.useState(get);
  React.useEffect(() => {
    const mq = window.matchMedia?.(MOBILE_QUERY);
    if (!mq) return;
    const on = () => setMob(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return mob;
}

/**
 * Nästa index för piltangenter i en rad (Tabs, Segmented). Hoppar över `disabled`.
 * Returnerar null om tangenten inte är en navigeringstangent.
 */
export function nextIndex(key: string, current: number, disabled: boolean[]): number | null {
  const n = disabled.length;
  if (n === 0) return null;
  const step = (from: number, dir: 1 | -1) => {
    for (let k = 1; k <= n; k++) {
      const i = (from + dir * k + n * k) % n;
      if (!disabled[i]) return i;
    }
    return current;
  };
  switch (key) {
    case 'ArrowRight': case 'ArrowDown': return step(current, 1);
    case 'ArrowLeft': case 'ArrowUp': return step(current, -1);
    case 'Home': { const i = disabled.indexOf(false); return i < 0 ? current : i; }
    case 'End': { const i = disabled.lastIndexOf(false); return i < 0 ? current : i; }
    default: return null;
  }
}

/** Escape stänger, fokus flyttas in när den öppnas och tillbaka när den stängs. */
export function useOverlay(open: boolean, onClose: () => void, ref: React.RefObject<HTMLElement>) {
  const closeRef = React.useRef(onClose);
  closeRef.current = onClose;
  React.useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>('[data-autofocus], button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    (first ?? el)?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); closeRef.current(); } };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); prev?.focus?.({ preventScroll: true }); };
  }, [open, ref]);
}

export const baseButton: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  border: '1px solid transparent', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
  lineHeight: 1, textDecoration: 'none', WebkitTapHighlightColor: 'transparent',
};
